"use client";

import { DateTime } from "luxon";
import { useSearchParams } from "next/navigation";
import { ReactNode, Suspense, useState } from "react";
import { GetAllEventsOutput } from "~/types/EventTypesInferred";
import { api } from "~/utils/api";
import { cn } from "~/utils/fontsConfig";
import { adjustedTimeZoneToClient } from "~/utils/time";
import { If } from "../Conditional";
import PinnedTag from "./PinnedTag";
import {
    STRIPE_CURRENCIES,
    StripeCurrency,
} from "~/utils/stripe_currency_codes";

interface DetailsProps {
    event: GetAllEventsOutput;
}

export const EventDetailsText = ({ event }: DetailsProps) => {
    const searchParams = useSearchParams();
    const communitySlug = searchParams?.get("communitySlug");

    const startTime = event.isVirtual
        ? adjustedTimeZoneToClient(event.startTime, event.timezone)
        : DateTime.fromJSDate(event.startTime, {
              zone: "UTC",
          });

    const { weekdayShort, monthShort, day, year } = startTime;
    const time = Intl.DateTimeFormat("en-US", {
        timeZone: "UTC",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
    }).format(startTime.toJSDate());

    const {
        data: currentUserIsMember,
        isLoading: isLoadingCurrentUserIsMember,
    } = api.membership.isCurrentUserMember.useQuery(
        {
            communitySlug: communitySlug as string,
        },
        {
            enabled: !!communitySlug,
        },
    );

    const renderTime = () => {
        if (!event.isTimeSet) return <> • TBD</>;
        if (event.isMembersOnly) {
            if (isLoadingCurrentUserIsMember) {
                return null;
            }

            if (currentUserIsMember) {
                return <> • {time}</>;
            }
        } else {
            return <> • {time}</>;
        }
    };

    return (
        <>
            {event.isTimeSet ? (
                <>
                    {weekdayShort}, {monthShort} {day}
                    {renderTime()}
                </>
            ) : (
                <>{year} • TBD</>
            )}
        </>
    );
};

const TagStyle = ({
    children,
    className,
}: {
    children?: ReactNode;
    className?: string;
}) => {
    return (
        <div
            className={cn(
                `
                flex 
                h-8 
                max-w-max 
                items-center
                justify-center
                rounded-[8px]
                border-v2-primary 
                bg-white 
                px-2 
                py-1 
                leading-none 
                text-v2-primary 
                shadow-xl
            `,
                className,
            )}
        >
            {children}
        </div>
    );
};

export const EventDetailsTag = ({
    event,
    showPrice = true,
    className,
}: {
    event: GetAllEventsOutput;
    showPrice?: boolean;
    className?: string;
}) => {
    let shouldShowPriceText = false;
    if (event?.priceDollars && event?.priceDollars >= 0) {
        shouldShowPriceText = true;
    }
    const defaultCurrency = STRIPE_CURRENCIES[0];
    const initialCurrency =
        STRIPE_CURRENCIES.find((c) => c.code === event.currencyCode) ??
        defaultCurrency;
    const [currency, setCurrency] = useState<StripeCurrency>(
        initialCurrency as StripeCurrency,
    );

    return (
        <div className={cn("absolute z-30 flex w-full justify-between p-2")}>
            <TagStyle className="min-w-[178px]">
                <div className="flex flex-row">
                    <span
                        className={cn(
                            "text-[14px] font-[500] leading-relaxed",
                            className,
                        )}
                    >
                        <Suspense>
                            <EventDetailsText event={event} />
                        </Suspense>
                    </span>
                </div>
            </TagStyle>
            <If condition={shouldShowPriceText}>
                <TagStyle>
                    <span
                        className={cn(
                            "flex flex-row items-center text-[14px] font-[500] leading-relaxed text-success",
                            className,
                        )}
                    >
                        {currency.icon} {event.priceDollars}
                    </span>
                </TagStyle>
            </If>

            <PinnedTag event={event} />
        </div>
    );
};
